import { z } from 'zod';
import { WoWarning } from '../api-contracts.js';
import { SourceSystem } from '../enums.js';
import { DecimalString, IsoDateTime, MoneyAmount, UuidString } from '../primitives.js';

export const WorkOrderStatus = z.enum([
  'draft',
  'planned',
  'released',
  'in_progress',
  'paused_external',
  'completed',
  'closed',
  'cancelled',
]);
export type WorkOrderStatus = z.infer<typeof WorkOrderStatus>;

/**
 * U15.1 — русские labels для WorkOrderStatus, shared между WoStatePill,
 * list-page filter, error-code localization. DRY одним источником.
 */
export const WORK_ORDER_STATUS_LABEL: Record<WorkOrderStatus, string> = {
  draft: 'Черновик',
  planned: 'Запланирован',
  released: 'Выпущен',
  in_progress: 'В работе',
  paused_external: 'На паузе',
  completed: 'Завершён',
  closed: 'Закрыт',
  cancelled: 'Отменён',
};

/**
 * U14.1 — canonical list-item shape (backend GET /work-orders).
 */
export const WoListItem = z
  .object({
    id: UuidString,
    // F-sync — denorm колонка «Компания» в all-scope.
    entity_id: UuidString.optional(),
    code: z.string(),
    item_id: UuidString,
    item_name: z.string().nullable().optional(),
    bom_id: UuidString.nullable().optional(),
    bom_version: z.string().nullable().optional(),
    qty: DecimalString,
    state: WorkOrderStatus,
    planned_start_at: IsoDateTime.nullable(),
    planned_end_at: IsoDateTime.nullable(),
    executor_id: UuidString.nullable().optional(),
    executor_name: z.string().nullable().optional(),
    warnings_count: z.number().int().nonnegative().default(0),
  })
  .passthrough();
export type WoListItem = z.infer<typeof WoListItem>;

/** Work Order — lifecycle managed as Temporal workflow. */
// S-FIX-4: BE list handler omits internal fields; made optional for prod parity.
export const WorkOrder = z.object({
  id: UuidString,
  entity_id: UuidString.optional(),
  voucher_no: z.string().min(1).optional(),
  item_id: UuidString.optional(),
  bom_id: UuidString.optional(),
  qty_to_produce: DecimalString.optional(),
  qty_produced: DecimalString.default('0'),
  qty_scrapped: DecimalString.default('0'),
  status: WorkOrderStatus.default('draft'),
  source_warehouse_id: UuidString.nullable().optional(),
  scrap_warehouse_id: UuidString.nullable().optional(),
  planned_start_date: IsoDateTime.nullable().optional(),
  planned_end_date: IsoDateTime.nullable().optional(),
  actual_start_date: IsoDateTime.nullable().optional(),
  actual_end_date: IsoDateTime.nullable().optional(),
  workflow_id: z.string().nullable().optional(),
  source_system: SourceSystem.default('staiter'),
  created_at: IsoDateTime.optional(),
  updated_at: IsoDateTime.optional(),
});
export type WorkOrder = z.infer<typeof WorkOrder>;

/** Job Card — одна operation в routing. */
export const JobCard = z.object({
  id: UuidString,
  entity_id: UuidString.optional(),
  work_order_id: UuidString,
  operation_id: UuidString.nullable(),
  operation_name: z.string().min(1),
  workstation: z.string().nullable(),
  position: z.number().int().nonnegative(),
  planned_qty: DecimalString,
  completed_qty: DecimalString.default('0'),
  scrapped_qty: DecimalString.default('0'),
  time_minutes_planned: DecimalString,
  time_minutes_actual: DecimalString.default('0'),
  status: z.enum(['open', 'in_progress', 'submitted', 'completed', 'cancelled']).default('open'),
  actual_cost: MoneyAmount.nullable(),
  /** W10.2 — resolveUsers(operatorUserId) JOIN. */
  operator_user_id: UuidString.nullable().optional(),
  operator_name: z.string().nullable().optional(),
  operator_email: z.string().nullable().optional(),
  created_at: IsoDateTime,
  updated_at: IsoDateTime,
});
export type JobCard = z.infer<typeof JobCard>;

/** F110 пака `tails-closeout` (С-I): итог закрытого процесса заказа в Temporal. */
/** F104 (С-I): документ выпуска или его сторно в карточке отменённого заказа. */
export const WoCancelOutputDoc = z
  .object({ id: UuidString, voucher_no: z.string(), is_submitted: z.boolean() })
  .strict();
export type WoCancelOutputDoc = z.infer<typeof WoCancelOutputDoc>;

export const WoExecutionStopCause = z.enum([
  'completed',
  'failed',
  'cancelled',
  'terminated',
  'timed_out',
  'not_found',
]);
export type WoExecutionStopCause = z.infer<typeof WoExecutionStopCause>;

/**
 * W1.4 — Canonical `WoDetail` — shape, который возвращает backend `shapeWoDetail`
 * (apps/backend/src/api/work-orders.ts). Заменяет legacy `WoDetail` из `legacy-ui-contracts.ts`.
 *
 * `.passthrough()` — NOT `.strict()`: backend может добавлять поля (W10.1/10.4/10.5
 * JOIN enrichment, workflow_id, signal_sent/signal_at в response-shape aliases),
 * FE должен игнорировать unknown без ZodError (см. B6 из предыдущего хардинга).
 */
export const WoDetail = z
  .object({
    id: UuidString,
    code: z.string(),
    item_id: UuidString,
    item_name: z.string().nullable().optional(),
    item_uom: z.string().nullable().optional(),
    item_sku: z.string().nullable().optional(),
    bom_id: UuidString,
    bom_name: z.string().nullable().optional(),
    bom_code: z.string().nullable().optional(),
    bom_version: z.string().nullable().optional(),
    qty: DecimalString,
    qty_produced: DecimalString,
    state: WorkOrderStatus,
    planned_start_at: IsoDateTime.nullable(),
    planned_end_at: IsoDateTime.nullable(),
    actual_start_at: IsoDateTime.nullable(),
    actual_end_at: IsoDateTime.nullable(),
    executor_id: UuidString.nullable(),
    executor_name: z.string().nullable().optional(),
    executor_email: z.string().nullable().optional(),
    source_warehouse_id: UuidString.nullable(),
    source_warehouse_name: z.string().nullable().optional(),
    wip_warehouse_id: UuidString.nullable(),
    wip_warehouse_name: z.string().nullable().optional(),
    fg_warehouse_id: UuidString.nullable(),
    fg_warehouse_name: z.string().nullable().optional(),
    scrap_warehouse_id: UuidString.nullable().optional(),
    scrap_warehouse_name: z.string().nullable().optional(),
    warnings: z.array(WoWarning).default([]),
    cost: z
      .object({
        variance: z.unknown().nullable().optional(),
        variance_pct: z.number().nullable().optional(),
        planned_cost: z.unknown().nullable().optional(),
        actual_cost: z.unknown().nullable().optional(),
        variance_amount: z.unknown().nullable().optional(),
        reason: z.string().nullable().optional(),
      })
      .passthrough()
      .nullable()
      .optional(),
    job_cards: z.array(JobCard).default([]),
    workflow_id: z.string().nullable().optional(),
    /**
     * F110 (С-I): исполнение остановлено — процесс заказа закрыт в Temporal, а статус не конечный
     * (действия с карточки вернут 409 `WORKFLOW_CLOSED`); null — Temporal не ответил, неизвестно.
     */
    execution_stopped: z.boolean().nullable().optional(),
    /** Круг 3 гейта F110 (UX): когда процесс закрылся в Temporal; null — неизвестно (не найден). */
    execution_stopped_at: IsoDateTime.nullable().optional(),
    /** Итог закрытого процесса; `not_found` — Temporal процесс уже не помнит. */
    execution_stop_cause: WoExecutionStopCause.nullable().optional(),
    /**
     * F104 пака `tails-closeout` (С-I) — документы выпуска ОТМЕНЁННОГО заказа: что отмена сделала со
     * складом. `reversal` — обратный документ (`manufacture_reversal`), его создаёт отмена уровня 3;
     * `is_submitted: false` — черновик, склад им ещё не изменён. `null` у неотменённых.
     */
    cancel_output: z
      .object({
        manufacture: WoCancelOutputDoc.nullable(),
        reversal: WoCancelOutputDoc.nullable(),
        /** Роли, которые проводят обратный документ (`stock_entry:approve`, без администраторов). */
        submit_roles: z.array(z.string()),
      })
      .strict()
      .nullable()
      .optional(),
    original_manufacture_se_id: UuidString.nullable().optional(),
    /** W0.1 — signal aliases response добавляют метаданные о последнем signal. */
    signal_sent: z.string().optional(),
    signal_at: IsoDateTime.optional(),
    created_at: IsoDateTime,
    updated_at: IsoDateTime,
  })
  .passthrough();
export type WoDetail = z.infer<typeof WoDetail>;
